<template>
    <modal id="b2b-pricelist-modal" size="extra-large" v-model="showModal"
        :title="$t('Pricelist') + (partner && partner.name ? ' — ' + partner.name : '')" :preloader="false"
        @input="closeModal">
        <div>
            <!-- Category filter: which product categories to search within. -->
            <div class="mb-3">
                <label class="form-label mb-1 d-block">{{ $t('Search in categories') }}</label>
                <div class="d-flex flex-wrap" style="gap: 1.5rem;">
                    <div class="form-check" v-for="c in categoryList" :key="c.id">
                        <input class="form-check-input" type="checkbox" :id="'pl-cat-' + c.id" :value="c.id"
                            v-model="selectedCategories">
                        <label class="form-check-label" :for="'pl-cat-' + c.id">{{ c.name }}</label>
                    </div>
                </div>
            </div>

            <!-- Add a product + special price. HSN/SAC and GST% come from the master. -->
            <div class="row align-items-end mb-3">
                <div class="col-12 col-md-5 mb-2 position-relative">
                    <label class="form-label">{{ $t('Product') }}</label>
                    <input type="text" class="form-control" v-model="variantSearch" autocomplete="off"
                        :placeholder="$t('Type a product name…')" @input="onVariantSearch"
                        @focus="onVariantSearch" @blur="hideResultsSoon" />
                    <div v-if="showResults && variantResults.length"
                        class="border rounded bg-white position-absolute shadow-sm"
                        style="z-index: 1085; max-height: 240px; overflow-y: auto; left: 15px; right: 15px;">
                        <a v-for="v in variantResults" :key="v.id" href="#"
                            class="d-block px-3 py-2 text-dark" style="text-decoration: none;"
                            @mousedown.prevent="selectVariant(v)">{{ v.name }}</a>
                    </div>
                    <div v-if="showResults && !variantResults.length && variantSearch"
                        class="border rounded bg-white position-absolute shadow-sm px-3 py-2 text-muted"
                        style="z-index: 1085; left: 15px; right: 15px;">
                        {{ $t('No matching products.') }}
                    </div>
                </div>
                <div class="col-6 col-md-2 mb-2">
                    <label class="form-label">{{ $t('HSN/SAC') }}</label>
                    <input type="text" class="form-control" :value="selectedInfo.hsnsac" readonly placeholder="—" />
                </div>
                <div class="col-6 col-md-2 mb-2">
                    <label class="form-label">{{ $t('GST %') }}</label>
                    <input type="text" class="form-control"
                        :value="(selectedInfo.gst !== null && selectedInfo.gst !== undefined) ? selectedInfo.gst + '%' : ''"
                        readonly placeholder="—" />
                </div>
                <div class="col-6 col-md-2 mb-2">
                    <label class="form-label">{{ $t('Special Price') }}</label>
                    <input type="number" min="0" step="0.01" class="form-control" v-model="newPrice" placeholder="0.00" />
                </div>
                <div class="col-6 col-md-1 mb-2">
                    <button type="button" class="btn btn-primary btn-block" @click="addPrice">{{ $t('Add') }}</button>
                </div>
            </div>

            <!-- Import / template -->
            <div class="row align-items-end mb-3 border-top pt-3">
                <div class="col-12 col-md-7">
                    <label class="form-label">{{ $t('Bulk import — download the template, fill the price column, re-upload') }}</label>
                    <input type="file" class="form-control" accept=".xlsx,.xls,.csv" @change="onFileChange" ref="fileInput" />
                </div>
                <div class="col-6 col-md-2">
                    <button type="button" class="btn btn-success btn-block" :disabled="!importFile || importing" @click="doImport">
                        {{ importing ? $t('Importing…') : $t('Import') }}
                    </button>
                </div>
                <div class="col-6 col-md-3">
                    <button type="button" class="btn btn-outline-primary btn-block" @click="downloadTemplate">
                        {{ $t('Download template') }}
                    </button>
                </div>
            </div>

            <!-- Current pricelist -->
            <div class="table-responsive">
                <table class="table table-sm table-hover mb-0">
                    <thead>
                        <tr>
                            <th>{{ $t('Product') }}</th>
                            <th style="width:130px;">{{ $t('HSN/SAC') }}</th>
                            <th style="width:90px;">{{ $t('GST %') }}</th>
                            <th style="width:150px;">{{ $t('Special Price') }}</th>
                            <th style="width:60px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="!prices.length">
                            <td colspan="5" class="text-center text-muted">{{ $t('No special prices yet.') }}</td>
                        </tr>
                        <tr v-for="row in prices" :key="row.id">
                            <td>{{ row.variant_name }}</td>
                            <td>{{ row.hsnsac || '—' }}</td>
                            <td>{{ (row.gst !== null && row.gst !== undefined) ? row.gst + '%' : '—' }}</td>
                            <td>
                                <input type="number" min="0" step="0.01" class="form-control form-control-sm"
                                    v-model="row.price" @blur="updatePrice(row)" />
                            </td>
                            <td>
                                <button type="button" class="btn btn-danger btn-sm" @click="removePrice(row)">
                                    <app-icon name="trash-2" class="size-15" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Single OK to exit; every add/edit/remove already saves on its own. -->
        <template #footer>
            <button type="button" class="btn btn-primary" data-dismiss="modal" @click="onOk">{{ $t('OK') }}</button>
        </template>
    </modal>
</template>

<script>
import ModalMixin from "../../../../common/Mixin/Global/ModalMixin";
import { axiosGet, axiosPost, axiosDelete, urlGenerator } from "../../../../common/Helper/AxiosHelper";
import { B2B_PARTNER_PRICES } from "../../../Config/ApiUrl-CPB";

export default {
    name: "B2bPartnerPricelistModal",
    mixins: [ModalMixin],
    props: { partner: { type: Object, default: () => ({}) } },
    data() {
        return {
            prices: [],
            // Category filter (main_category codes). Raw Material + Ready to use
            // are unchecked by default.
            categoryList: [
                { id: 1, name: this.$t('Raw Material') },
                { id: 2, name: this.$t('Ready to use') },
                { id: 3, name: this.$t('Custom Products') },
                { id: 4, name: this.$t('Service') },
            ],
            selectedCategories: [3, 4],
            // Product typeahead
            variantSearch: '',
            variantResults: [],
            showResults: false,
            searchTimer: null,
            // Selected product
            newVariant: '',
            selectedInfo: { hsnsac: '', gst: null, name: '' },
            newPrice: '',
            // Import
            importFile: null,
            importing: false,
        };
    },
    created() {
        this.fetchPrices();
    },
    watch: {
        selectedCategories() {
            // Refresh the open result list when the category selection changes.
            if (this.showResults) this.onVariantSearch();
        },
    },
    methods: {
        partnerId() {
            return this.partner ? this.partner.id : null;
        },
        // Every add/edit/remove already saves immediately, so OK just closes the
        // modal. Also clear any leftover Bootstrap backdrop so the page never freezes.
        onOk() {
            this.showModal = false;
            this.$emit('input', false);
            this.$emit('close');
            this.$nextTick(() => {
                try {
                    $('#b2b-pricelist-modal').modal('hide');
                    $('.modal-backdrop').remove();
                    $('body').removeClass('modal-open').css('padding-right', '');
                } catch (e) { /* noop */ }
            });
        },
        fetchPrices() {
            axiosGet(`${B2B_PARTNER_PRICES}?b2b_partner_id=${this.partnerId()}`).then(res => {
                this.prices = res.data || [];
            }).catch(() => {});
        },
        // ---- Product typeahead ----
        onVariantSearch() {
            this.showResults = true;
            clearTimeout(this.searchTimer);
            const q = (this.variantSearch || '').trim();
            const cats = (this.selectedCategories || []).join(',');
            this.searchTimer = setTimeout(() => {
                axiosGet(`${B2B_PARTNER_PRICES}/search-products?q=${encodeURIComponent(q)}&categories=${cats}`)
                    .then(res => {
                        this.variantResults = Array.isArray(res.data) ? res.data : [];
                    })
                    .catch(() => { this.variantResults = []; });
            }, 250);
        },
        hideResultsSoon() {
            // Delay so a click on a result registers before the list hides.
            setTimeout(() => { this.showResults = false; }, 200);
        },
        selectVariant(v) {
            this.newVariant = v.id;
            this.variantSearch = v.name;
            this.showResults = false;
            // HSN/SAC + GST% + default price come inline with the search result.
            this.selectedInfo = { hsnsac: v.hsnsac || '', gst: (v.gst ?? null), name: v.name || '' };
            if (this.newPrice === '' || this.newPrice === null) this.newPrice = v.selling_price || '';
        },
        resetAddRow() {
            this.newVariant = '';
            this.variantSearch = '';
            this.newPrice = '';
            this.selectedInfo = { hsnsac: '', gst: null, name: '' };
            this.variantResults = [];
        },
        // ---- Pricelist CRUD ----
        addPrice() {
            if (!this.newVariant || this.newPrice === '' || this.newPrice === null) {
                this.$toastr.e('Select a product and enter a price.');
                return;
            }
            const data = new FormData();
            data.append('b2b_partner_id', this.partnerId());
            data.append('variant_id', this.newVariant);
            data.append('price', this.newPrice);
            axiosPost(B2B_PARTNER_PRICES, data).then(() => {
                this.resetAddRow();
                this.fetchPrices();
                this.$toastr.s('Price saved.');
            }).catch(({ response }) => {
                this.$toastr.e(response && response.data ? response.data.message : 'Could not save.');
            });
        },
        updatePrice(row) {
            const data = new FormData();
            data.append('b2b_partner_id', this.partnerId());
            data.append('variant_id', row.variant_id);
            data.append('price', row.price);
            axiosPost(B2B_PARTNER_PRICES, data).then(() => {
                this.$toastr.s('Price updated.');
            }).catch(() => { this.$toastr.e('Could not update.'); });
        },
        removePrice(row) {
            axiosDelete(`${B2B_PARTNER_PRICES}/${row.id}`).then(() => {
                this.prices = this.prices.filter(p => p.id !== row.id);
            }).catch(() => { this.$toastr.e('Could not remove.'); });
        },
        // ---- Import ----
        onFileChange(e) {
            this.importFile = e.target.files[0] || null;
        },
        doImport() {
            if (!this.importFile) return;
            this.importing = true;
            const data = new FormData();
            data.append('b2b_partner_id', this.partnerId());
            data.append('file', this.importFile);
            axiosPost(`${B2B_PARTNER_PRICES}/import`, data).then(() => {
                this.importFile = null;
                if (this.$refs.fileInput) this.$refs.fileInput.value = '';
                this.fetchPrices();
                this.$toastr.s('Pricelist imported.');
            }).catch(({ response }) => {
                this.$toastr.e(response && response.data ? response.data.message : 'Import failed.');
            }).finally(() => { this.importing = false; });
        },
        downloadTemplate() {
            window.open(urlGenerator(`${B2B_PARTNER_PRICES}/template?b2b_partner_id=${this.partnerId()}`), '_blank');
        },
    },
};
</script>
